import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { API_URL, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Star,
  StarHalf,
  ThumbsUp,
  User,
  Building2,
  ArrowLeft,
  Loader2,
  PenLine,
} from 'lucide-react';

// Star Rating Component
const StarRating = ({ rating, size = 'md', interactive = false, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const filled = interactive ? (hovered || rating) >= i : rating >= i;
    const halfFilled = !filled && rating >= i - 0.5;
    
    stars.push(
      <button
        key={i}
        type="button"
        disabled={!interactive}
        className={`${interactive ? 'cursor-pointer hover:scale-110' : ''} transition-transform`}
        onMouseEnter={() => interactive && setHovered(i)}
        onMouseLeave={() => interactive && setHovered(0)}
        onClick={() => interactive && onChange?.(i)}
      >
        {halfFilled ? (
          <StarHalf className={`${sizeClass} text-secondary fill-secondary`} />
        ) : (
          <Star className={`${sizeClass} ${filled ? 'text-secondary fill-secondary' : 'text-gray-300'}`} />
        )}
      </button>
    );
  }
  
  return <div className="flex items-center gap-0.5">{stars}</div>;
};

// Employer Reviews Page
const EmployerReviews = () => {
  const { employerId } = useParams();
  const navigate = useNavigate();
  const { user, isJobSeeker } = useAuth();
  
  const [reviews, setReviews] = useState([]);
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    title: '',
    review_text: '',
    would_recommend: true,
  });

  useEffect(() => {
    fetchReviews();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/employer/${employerId}`);
      setReviews(response.data.reviews);
      setEmployer(response.data.employer);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (reviewForm.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (!reviewForm.title.trim() || !reviewForm.review_text.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/reviews`,
        {
          employer_id: employerId,
          ...reviewForm,
        },
        { headers: {  } }
      );
      
      toast.success('Review submitted successfully!');
      setShowWriteReview(false);
      setReviewForm({ rating: 0, title: '', review_text: '', would_recommend: true });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Employer Header */}
        <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="font-serif text-2xl font-bold text-gray-900">
                {employer?.company_name || employer?.name || 'Employer'}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                {employer?.avg_rating ? (
                  <div className="flex items-center gap-2">
                    <StarRating rating={employer.avg_rating} />
                    <span className="font-semibold text-gray-900">{employer.avg_rating}</span>
                    <span className="text-gray-500">({employer.review_count} reviews)</span>
                  </div>
                ) : (
                  <span className="text-gray-500">No reviews yet</span>
                )}
              </div>
            </div>
            {isJobSeeker && (
              <Button
                onClick={() => setShowWriteReview(true)}
                className="bg-secondary hover:bg-secondary-hover text-white"
              >
                <PenLine className="w-4 h-4 mr-2" />
                Write Review
              </Button>
            )}
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900">
            {pagination.total} {pagination.total === 1 ? 'Review' : 'Reviews'}
          </h2>
          
          {reviews.length > 0 ? (
            reviews.map((review) => (
              <div key={review.review_id} className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{review.reviewer_name}</p>
                      <p className="text-sm text-gray-500">{formatDate(review.created_at)}</p>
                    </div>
                  </div>
                  <StarRating rating={review.rating} size="sm" />
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-2">{review.title}</h3>
                <p className="text-gray-600 mb-4">{review.review_text}</p>
                
                {review.would_recommend && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600">
                    <ThumbsUp className="w-4 h-4" />
                    <span>Would recommend this employer</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">No reviews yet</h3>
              <p className="text-gray-500">Be the first to review this employer</p>
            </div>
          )}
        </div>
      </div>

      {/* Write Review Dialog */}
      <Dialog open={showWriteReview} onOpenChange={setShowWriteReview}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Write a Review</DialogTitle>
            <DialogDescription>
              Share your experience working with {employer?.company_name || employer?.name}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitReview} className="space-y-6 py-4">
            <div>
              <Label className="mb-2 block">Rating *</Label>
              <StarRating
                rating={reviewForm.rating}
                size="lg"
                interactive
                onChange={(rating) => setReviewForm({ ...reviewForm, rating })}
              />
            </div>

            <div>
              <Label htmlFor="title">Review Title *</Label>
              <Input
                id="title"
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                placeholder="Summarize your experience"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="review_text">Your Review *</Label>
              <Textarea
                id="review_text"
                value={reviewForm.review_text}
                onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                placeholder="Share details about your experience..."
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="would_recommend"
                checked={reviewForm.would_recommend}
                onCheckedChange={(checked) => setReviewForm({ ...reviewForm, would_recommend: checked })}
              />
              <Label htmlFor="would_recommend" className="cursor-pointer">
                I would recommend this employer
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowWriteReview(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary-hover text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { EmployerReviews, StarRating };
